import type { BatchInspectResult, InspectionResult } from '@/lib/types';
import { buildCsv } from '@/app/pages/assets/batch-entry/batch-utils';
import type { BatchLine, BatchLineOutcome } from '@/app/pages/assets/batch-entry/batch-line-utils';

export interface InspectLine extends BatchLine {
  result: InspectionResult;
  failNotes: string;
}

export function inspectOutcome(result: BatchInspectResult): BatchLineOutcome {
  if (result.status !== 'created') {
    return {
      status: 'error',
      label: 'Failed',
      message: result.error ?? 'Could not record this inspection.',
      tone: 'error',
    };
  }
  if (result.result === 'fail') {
    return {
      status: 'failed_inspection',
      label: 'Fail — quarantined',
      message: result.asset_status === 'quarantined'
        ? 'Failed inspection recorded and signed; asset quarantined.'
        : 'Failed inspection recorded and signed.',
      tone: 'warning',
    };
  }
  return {
    status: 'passed',
    label: result.result === 'conditional' ? 'Conditional' : 'Pass',
    message: result.next_inspection_due
      ? `Signed. Next inspection due ${result.next_inspection_due}.`
      : 'Signed.',
    tone: 'success',
  };
}

export function buildBatchInspectCsv(batchId: string, batchName: string, lines: InspectLine[]): string {
  return buildCsv(
    ['batch_id', 'batch_name', 'serial', 'asset', 'category', 'result', 'fail_notes', 'status', 'message', 'asset_id'],
    lines.map((line) => [
      batchId,
      batchName,
      line.serial,
      line.resolved?.asset?.display_name ?? '',
      line.resolved?.asset?.type_display ?? '',
      line.resolved?.status === 'ready' ? line.result : '',
      line.failNotes,
      line.outcome?.status ?? (line.resolved ? `not_recorded_${line.resolved.status}` : 'not_recorded'),
      line.outcome?.message ?? line.resolved?.message ?? '',
      line.resolved?.asset?.id ?? '',
    ])
  );
}

/**
 * Why "Sign & record" cannot run right now, or null when it can. The sign-off
 * name and PIN are only checked here, at the final step — never while lines
 * are being added or marked Pass/Fail.
 */
export function batchInspectSignBlockReason({
  detailsReady,
  signerName,
  requiresPin,
  pin,
  readyCount,
  resolvingCount,
}: {
  detailsReady: boolean;
  signerName: string;
  requiresPin: boolean;
  pin: string;
  readyCount: number;
  resolvingCount: number;
}): string | null {
  if (!detailsReady) return 'Enter the batch name and inspection date first (step 1).';
  if (resolvingCount > 0) {
    return `Still looking up ${resolvingCount} serial${resolvingCount === 1 ? '' : 's'} — try again in a moment.`;
  }
  if (readyCount === 0) return 'Add at least one matched item before signing.';
  if (!signerName.trim()) return 'Type your full name to sign the batch.';
  if (requiresPin && !pin) return 'Enter your signing PIN to sign the batch.';
  return null;
}
